import { createFileRoute } from "@tanstack/react-router";
import { isAcceptableUsername } from "@/lib/osint/person/username";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AppLayout } from "@/components/intel/Layout";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CompanyCard, OrganizationContacts } from "@/components/osint/CompanyContactsPanel";
import { PublicContactsPanel } from "@/components/intel/PublicContactsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  runPersonIntel,
  listPersonIntelReports,
  getPersonIntelReport,
  getPersonSourceHealth,
} from "@/lib/osint/person/person.functions";
import type { PersonEvidence, PersonIntelReport } from "@/lib/osint/person/types";
import { ExternalLink, Loader2, UserSearch } from "lucide-react";

export const Route = createFileRoute("/person")({
  head: () => ({
    meta: [
      { title: "Person Intelligence · CARIO Nexus" },
      {
        name: "description",
        content:
          "Resolve a person from public sources only: identity clusters with deterministic confidence, public contact references, employment and corporate roles, profiles, documents, news and a provenance-backed graph.",
      },
      { property: "og:title", content: "Person Intelligence · CARIO Nexus" },
      {
        property: "og:description",
        content: "Public-source person investigation with identity resolution, contradiction detection and full evidence provenance.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search["q"] === "string" && search["q"] ? { q: search["q"] } : {},
  component: PersonIntelPage,
});

const BAND_STYLE: Record<string, string> = {
  very_high: "bg-success/15 text-success border-success/40",
  high: "bg-success/10 text-success border-success/30",
  medium: "bg-cyan/15 text-cyan border-cyan/40",
  low: "bg-amber/15 text-amber border-amber/40",
  very_low: "bg-muted/30 text-muted-foreground border-panel-border",
};

const CLASS_STYLE: Record<string, string> = {
  FACT: "bg-success/15 text-success border-success/40",
  CORRELATION: "bg-cyan/15 text-cyan border-cyan/40",
  UNVERIFIED_CANDIDATE: "bg-amber/15 text-amber border-amber/40",
  CONFLICT: "bg-destructive/15 text-destructive border-destructive/40",
  NEXT_PIVOT: "bg-muted/30 text-muted-foreground border-panel-border",
};

function Chip({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] tracking-wider ${className || "border-panel-border text-muted-foreground"}`}>{children}</span>;
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="glass rounded-xl p-3 sm:p-4">
      <h2 className="font-display text-sm">{title}</h2>
      {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="rounded-md border border-dashed border-panel-border p-4 text-center text-xs text-muted-foreground">{children}</p>;
}

function EvidenceList({ items }: { items: PersonEvidence[] }) {
  if (!items.length) return null;
  return (
    <ul className="mt-2 space-y-1.5">
      {items.slice(0, 8).map((e) => (
        <li key={e.id} className="rounded-md border border-panel-border bg-background/40 p-2">
          <div className="flex flex-wrap items-center gap-2">
            <Chip className="border-cyan/40 text-cyan">{e.kind.replace(/_/g, " ")}</Chip>
            <span className="text-xs">{e.source}</span>
            <span className="ml-auto font-mono text-[10px] text-muted-foreground">
              {e.observedAt ? `seen ${e.observedAt}` : `retrieved ${e.retrievedAt.slice(0, 10)}`}
            </span>
          </div>
          {e.detail && <p className="mt-0.5 text-xs text-muted-foreground">{e.detail}</p>}
          {e.sourceUrl && (
            <a href={e.sourceUrl} target="_blank" rel="noreferrer noopener" className="mt-1 inline-flex items-center gap-1 break-all text-[11px] text-cyan hover:underline">
              <ExternalLink className="h-3 w-3 shrink-0" /> {e.sourceUrl}
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

function Row({
  title,
  meta,
  score,
  badge,
  children,
}: {
  title: React.ReactNode;
  meta?: React.ReactNode;
  score?: number;
  badge?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <details className="rounded-md border border-panel-border bg-background/40 p-2.5">
      <summary className="flex cursor-pointer flex-wrap items-center gap-2">
        <span className="min-w-0 break-words text-xs">{title}</span>
        {badge}
        {score !== undefined && <span className="ml-auto font-mono text-[11px] text-muted-foreground">{score}%</span>}
      </summary>
      {meta && <p className="mt-1 text-[11px] text-muted-foreground">{meta}</p>}
      {children}
    </details>
  );
}

function PersonIntelPage() {
  const { q } = Route.useSearch();
  const [form, setForm] = useState({
    name: q ?? "",
    city: "",
    country: "",
    company: "",
    occupation: "",
    username: "",
    email: "",
    phone: "",
  });
  const [report, setReport] = useState<PersonIntelReport | null>(null);

  const run = useServerFn(runPersonIntel);
  const list = useServerFn(listPersonIntelReports);
  const load = useServerFn(getPersonIntelReport);
  const health = useServerFn(getPersonSourceHealth);

  const history = useQuery({ queryKey: ["person-intel", "history"], queryFn: () => list() });
  const sourceHealth = useQuery({ queryKey: ["person-intel", "health"], queryFn: () => health(), staleTime: 5 * 60_000 });

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim())) as { name: string };
      return run({ data: { query: payload } });
    },
    onSuccess: (res) => {
      setReport(res.report);
      void history.refetch();
    },
  });

  const openReport = useMutation({
    mutationFn: (id: string) => load({ data: { id } }),
    onSuccess: (res) => setReport(res.report),
  });

  // Older stored reports can contain route tokens promoted as handles; the
  // validator filters them out at display time too.
  const cleanUsernames = useMemo(
    () => (report?.usernames ?? []).filter((u) => isAcceptableUsername(u.value)),
    [report],
  );
  const primary = useMemo(() => report?.clusters.find((c) => c.isPrimary), [report]);
  const companies = useMemo(() => report?.companies ?? [], [report]);
  // Direct professional contacts: only values a source tied to the person's
  // own name — never a company switchboard promoted to a personal line.
  const directProfessional = useMemo(() => {
    const out: { key: string; value: string; label: string; confidence: number; source: string; sourceUrl?: string }[] = [];
    for (const c of companies) {
      for (const [i, p] of c.phones.entries()) {
        if (p.context !== "PUBLIC_PROFESSIONAL_DIRECT") continue;
        out.push({
          key: `${c.id}-dp-${i}`,
          value: p.value,
          label: `direct line · ${c.name}`,
          confidence: p.confidence,
          source: p.source,
          ...(p.sourceUrl ? { sourceUrl: p.sourceUrl } : {}),
        });
      }
    }
    for (const p of report?.phones ?? []) {
      if (!/DIRECT|PERSONAL|PROFESSIONAL/i.test(p.context)) continue;
      out.push({
        key: p.id,
        value: p.number,
        label: p.context.replace(/_/g, " ").toLowerCase(),
        confidence: p.confidence,
        source: p.evidence[0]?.source ?? "Public source",
        ...(p.evidence[0]?.sourceUrl ? { sourceUrl: p.evidence[0]!.sourceUrl } : {}),
      });
    }
    return out;
  }, [companies, report]);
  const busy = mutation.isPending;

  return (
    <AppLayout>
      <ModuleShell
        eyebrow="Identity resolution"
        title="Person Intelligence"
        description="Native identity resolution across public registries, open data, the public web, documents and news. Every value carries its source."
      >
        <div className="space-y-4">
          <Panel title="Subject" subtitle="Only the name is required. Every extra detail sharpens identity resolution and lowers the chance of a same-name mix-up.">
            <form
              className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (form.name.trim().length >= 2 && !busy) mutation.mutate();
              }}
            >
              {([
                ["name", "Full name *"],
                ["city", "City"],
                ["country", "Country"],
                ["company", "Employer / company"],
                ["occupation", "Occupation"],
                ["username", "Known username"],
                ["email", "Known email"],
                ["phone", "Known phone"],
              ] as const).map(([key, label]) => (
                <label key={key} className="min-w-0 space-y-1">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
                  <Input
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    placeholder={label.replace(" *", "")}
                    className="h-9 text-sm"
                  />
                </label>
              ))}
              <div className="sm:col-span-2 lg:col-span-4">
                <Button type="submit" disabled={busy || form.name.trim().length < 2} className="w-full sm:w-auto">
                  {busy ? <Loader2 className="animate-spin" /> : <UserSearch />}
                  {busy ? "Resolving identity…" : "Run person intelligence"}
                </Button>
                {mutation.isError && (
                  <p className="mt-2 text-xs text-destructive">{(mutation.error as Error)?.message ?? "The run failed."}</p>
                )}
              </div>
            </form>
          </Panel>

          {report && (
            <>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
                <StatTile
                  label="Identity confidence"
                  value={typeof primary?.identityConfidence === "number" ? `${primary.identityConfidence}%` : "not calculated"}
                />
                <StatTile label="Identities found" value={String(report.clusters.length)} />
                <StatTile label="Evidence records" value={String(report.stats.evidenceRecords)} />
                <StatTile label="Public requests" value={String(report.stats.requests)} />
                <StatTile label="Runtime" value={`${Math.round(report.stats.runtimeMs / 100) / 10}s`} />
              </div>

              <Tabs defaultValue="overview">
                <div className="-mx-1 overflow-x-auto px-1">
                  <TabsList className="w-max">
                    {[
                      ["overview", "Overview"],
                      ["identity", "Identity"],
                      ["contact", "Contact"],
                      ["locations", "Locations"],
                      ["work", "Work & companies"],
                      ["public-contacts", "Public contacts"],
                      ["profiles", "Profiles"],
                      ["images", "Images"],
                      ["network", "Network"],
                      ["documents", "Documents"],
                      ["news", "News"],
                      ["timeline", "Timeline"],
                      ["evidence", "Evidence"],
                      ["graph", "Graph"],
                      ["ai", "AI analysis"],
                      ["sources", "Sources"],
                    ].map(([v, label]) => (
                      <TabsTrigger key={v} value={v} className="text-xs">
                        {label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>

                <TabsContent value="overview" className="mt-3 space-y-3">
                  <Panel title="Analyst summary">
                    <p className="text-sm leading-relaxed">{report.analyst.summary}</p>
                    {report.analyst.error && <p className="mt-2 text-[11px] text-amber">{report.analyst.error}</p>}
                  </Panel>
                  {report.contradictions.length > 0 && (
                    <Panel title="Contradictions" subtitle="Where sources disagree, and how CARIO reconciled them.">
                      <ul className="space-y-2">
                        {report.contradictions.map((c) => (
                          <li key={c.id} className="rounded-md border border-amber/30 bg-amber/5 p-2.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <Chip className="border-amber/40 text-amber">{c.verdict.replace(/_/g, " ")}</Chip>
                              <span className="text-xs">{c.summary}</span>
                            </div>
                            <ul className="mt-1.5 space-y-1 text-[11px] text-muted-foreground">
                              {c.sides.map((s, i) => (
                                <li key={`${c.id}-${i}`}>
                                  • {s.statement} — {s.source}
                                  {s.observedAt ? ` (${s.observedAt})` : ""}
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </Panel>
                  )}
                </TabsContent>

                <TabsContent value="identity" className="mt-3 space-y-3">
                  <Panel title="Identity clusters" subtitle="Same-name records are only merged when an identifier, employer, location or role matches.">
                    {report.clusters.length ? (
                      <div className="space-y-2">
                        {report.clusters.map((c) => (
                          <Row
                            key={c.id}
                            title={<span className="font-medium">{c.label}</span>}
                            badge={
                              <>
                                <Chip className={BAND_STYLE[c.confidenceBand] ?? ""}>{c.confidenceBand.replace(/_/g, " ")}</Chip>
                                {c.isPrimary && <Chip className="border-cyan/40 text-cyan">PRIMARY</Chip>}
                              </>
                            }
                            score={c.identityConfidence}
                            meta={[c.organization, c.role, c.locality, c.country].filter(Boolean).join(" · ")}
                          >
                            <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[11px] text-muted-foreground">
                              {c.confidenceReasons.map((r) => (
                                <li key={r}>{r}</li>
                              ))}
                            </ul>
                            {c.distinguishers.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {c.distinguishers.map((d) => (
                                  <Chip key={d}>{d}</Chip>
                                ))}
                              </div>
                            )}
                            {Object.keys(c.identifiers).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {Object.entries(c.identifiers).map(([k, v]) => (
                                  <Chip key={k} className="border-cyan/30 text-cyan">
                                    {k}: {v}
                                  </Chip>
                                ))}
                              </div>
                            )}
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No identity cluster reached a usable confidence level from public sources.</Empty>
                    )}
                  </Panel>
                  <Panel title="Name variants searched">
                    <div className="flex flex-wrap gap-1.5">
                      {report.variants.map((v) => (
                        <Chip key={`${v.kind}-${v.value}`}>
                          {v.value} <span className="opacity-60">· {v.kind.replace(/_/g, " ")}</span>
                        </Chip>
                      ))}
                    </div>
                  </Panel>
                </TabsContent>

                <TabsContent value="contact" className="mt-3 space-y-3">
                  <Panel
                    title="Direct professional contacts"
                    subtitle="Values a public source tied to the subject's own name."
                  >
                    {directProfessional.length ? (
                      <ul className="space-y-1.5">
                        {directProfessional.map((d) => (
                          <li key={d.key} className="rounded-md border border-panel-border bg-background/40 p-2 text-xs">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="break-all font-mono">{d.value}</span>
                              <Chip>{d.label}</Chip>
                              <span className="ml-auto font-mono text-[10px] text-muted-foreground">{Math.round(d.confidence)}%</span>
                            </div>
                            <p className="mt-0.5 text-[11px] text-muted-foreground">
                              {d.source}
                              {d.sourceUrl ? (
                                <>
                                  {" · "}
                                  <a href={d.sourceUrl} target="_blank" rel="noreferrer noopener" className="break-all text-cyan hover:underline">
                                    source
                                  </a>
                                </>
                              ) : null}
                            </p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Empty>No source tied a phone number or address directly to this person's own name.</Empty>
                    )}
                  </Panel>
                  <Panel
                    title="Organization contacts"
                    subtitle="Published contact details of the employer or associated company — not the person's private line."
                  >
                    {companies.some((c) => c.phones.length || c.emails.length || c.addresses.length) ? (
                      <OrganizationContacts companies={companies} />
                    ) : (
                      <Empty>No employer contact details were published by the sources checked.</Empty>
                    )}
                  </Panel>
                  <Panel title="Public email references" subtitle="Addresses published next to the subject's name on a public page or record.">
                    {report.emails.length ? (
                      <div className="space-y-2">
                        {report.emails.map((e) => (
                          <Row
                            key={e.id}
                            title={<span className="break-all font-mono">{e.address}</span>}
                            badge={<Chip>{e.verificationState.replace(/_/g, " ")}</Chip>}
                            score={e.confidence}
                          >
                            <EvidenceList items={e.evidence} />
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No email address was published in proximity to this name.</Empty>
                    )}
                  </Panel>
                  <Panel title="Public phone references" subtitle="Numbers found on public pages near the name. Not confirmed as a personal line.">
                    {report.phones.length ? (
                      <div className="space-y-2">
                        {report.phones.map((p) => (
                          <Row key={p.id} title={<span className="font-mono">{p.number}</span>} meta={p.context} score={p.confidence}>
                            <EvidenceList items={p.evidence} />
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No public phone reference was found.</Empty>
                    )}
                  </Panel>
                  <Panel title="Usernames">
                    {cleanUsernames.length ? (
                      <div className="space-y-2">
                        {cleanUsernames.map((u) => (
                          <Row key={u.id} title={<span className="font-mono">{u.value}</span>} meta={u.derivedFrom} score={u.confidence}>
                            <ul className="mt-2 space-y-1">
                              {u.accounts.map((a) => (
                                <li key={a.url}>
                                  <a href={a.url} target="_blank" rel="noreferrer noopener" className="break-all text-[11px] text-cyan hover:underline">
                                    {a.platform} — {a.url}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No username was confirmed on a live public profile.</Empty>
                    )}
                  </Panel>
                </TabsContent>

                <TabsContent value="public-contacts" className="mt-3">
                  <PublicContactsPanel result={report.publicContacts} />
                </TabsContent>

                <TabsContent value="locations" className="mt-3 space-y-3">
                  <Panel title="Address references" subtitle="Published address strings. CARIO never claims a current residence from a weak source.">
                    {report.addresses.length ? (
                      <div className="space-y-2">
                        {report.addresses.map((a) => (
                          <Row
                            key={a.id}
                            title={a.text}
                            badge={<Chip>{a.kind.replace(/_/g, " ")}</Chip>}
                            meta={a.context.replace(/_/g, " ").toLowerCase()}
                            score={a.confidence}
                          >
                            <EvidenceList items={a.evidence} />
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No address reference was published near this name.</Empty>
                    )}
                  </Panel>
                  <Panel title="Location signals">
                    {report.locations.length ? (
                      <ul className="space-y-1.5">
                        {report.locations.map((l) => (
                          <li key={l.id} className="rounded-md border border-panel-border bg-background/40 p-2 text-xs">
                            <div className="flex flex-wrap items-center gap-2">
                              <Chip>{l.kind.replace(/_/g, " ")}</Chip>
                              <span>{l.location}</span>
                              <span className="ml-auto font-mono text-[10px] text-muted-foreground">{l.confidence}%</span>
                            </div>
                            <p className="mt-0.5 text-[11px] text-muted-foreground">{l.context} · {l.source}</p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Empty>No location signal was found.</Empty>
                    )}
                  </Panel>
                </TabsContent>

                <TabsContent value="work" className="mt-3 space-y-3">
                  <Panel
                    title="Employers & companies"
                    subtitle="Organisation candidates resolved against official registries and enriched with public business contact information."
                  >
                    {companies.length ? (
                      <div className="space-y-3">
                        {companies.map((c) => (
                          <CompanyCard key={c.id} company={c} />
                        ))}
                      </div>
                    ) : (
                      <Empty>No organisation candidate reached the evidence threshold for company resolution.</Empty>
                    )}
                  </Panel>
                  <Panel title="Employment signals">
                    {report.employment.length ? (
                      <div className="space-y-2">
                        {report.employment.map((j) => (
                          <Row key={j.id} title={<span className="font-medium">{j.organization}</span>} meta={j.role} score={j.confidence}>
                            <EvidenceList items={j.evidence} />
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No source stated an employer for this person.</Empty>
                    )}
                  </Panel>
                  <Panel title="Corporate roles" subtitle="Officer and director records from public company registries.">
                    {report.corporateRoles.length ? (
                      <div className="space-y-2">
                        {report.corporateRoles.map((r) => (
                          <Row
                            key={r.id}
                            title={<span className="font-medium">{r.company}</span>}
                            badge={<Chip>{r.relation.replace(/_/g, " ")}</Chip>}
                            meta={[r.role, r.jurisdiction, r.start ? `from ${r.start}` : null, r.end ? `until ${r.end}` : null].filter(Boolean).join(" · ")}
                            score={r.confidence}
                          >
                            <EvidenceList items={r.evidence} />
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No corporate registry record matched this name.</Empty>
                    )}
                  </Panel>
                </TabsContent>

                <TabsContent value="profiles" className="mt-3">
                  <Panel title="Public profiles">
                    {report.profiles.length ? (
                      <div className="space-y-2">
                        {report.profiles.map((p) => (
                          <Row
                            key={p.id}
                            title={
                              <a href={p.url} target="_blank" rel="noreferrer noopener" className="break-all text-cyan hover:underline">
                                {p.platform} — {p.username ?? p.url}
                              </a>
                            }
                            meta={[p.displayName, p.organization, p.locationText, p.followers ? `${p.followers} followers` : null].filter(Boolean).join(" · ")}
                            score={p.confidence}
                          >
                            {p.bio && <p className="mt-1 text-xs text-muted-foreground">{p.bio}</p>}
                            <EvidenceList items={p.evidence} />
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No public profile was resolved.</Empty>
                    )}
                  </Panel>
                </TabsContent>

                <TabsContent value="images" className="mt-3">
                  <Panel title="Public images" subtitle="Images published on pages that name the subject. CARIO does not perform facial recognition here.">
                    {report.images.length ? (
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                        {report.images.map((img) => (
                          <a key={img.id} href={img.sourcePage} target="_blank" rel="noreferrer noopener" className="group rounded-md border border-panel-border bg-background/40 p-1.5">
                            <img src={img.imageUrl} alt={img.caption ?? "Public image"} loading="lazy" className="aspect-square w-full rounded object-cover" />
                            <p className="mt-1 line-clamp-2 text-[10px] text-muted-foreground group-hover:text-cyan">{img.context}</p>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <Empty>No public image was found on a page naming this person.</Empty>
                    )}
                  </Panel>
                </TabsContent>

                <TabsContent value="network" className="mt-3 space-y-3">
                  <Panel title="Related people" subtitle="Only surfaced when a shared document or registry record links them.">
                    {report.relatedPeople.length ? (
                      <div className="space-y-2">
                        {report.relatedPeople.map((r) => (
                          <Row key={r.id} title={r.name} badge={<Chip>{r.relation.replace(/_/g, " ")}</Chip>} meta={r.via} score={r.confidence}>
                            <EvidenceList items={r.evidence} />
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No corroborated connection to another person was found.</Empty>
                    )}
                  </Panel>
                  <Panel title="Related organisations">
                    {report.relatedOrganizations.length ? (
                      <div className="space-y-2">
                        {report.relatedOrganizations.map((o) => (
                          <Row key={o.id} title={o.name} badge={<Chip>{o.relation.replace(/_/g, " ")}</Chip>} score={o.confidence}>
                            <EvidenceList items={o.evidence} />
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No organisation was linked to this person.</Empty>
                    )}
                  </Panel>
                </TabsContent>

                <TabsContent value="documents" className="mt-3">
                  <Panel title="Documents" subtitle="PDFs and files that name the subject, with anything published alongside the name.">
                    {report.documents.length ? (
                      <div className="space-y-2">
                        {report.documents.map((d) => (
                          <Row
                            key={d.id}
                            title={
                              <a href={d.url} target="_blank" rel="noreferrer noopener" className="break-all text-cyan hover:underline">
                                {d.title ?? d.url}
                              </a>
                            }
                            badge={<Chip>{d.type.toUpperCase()}</Chip>}
                            meta={[d.source, d.publishedAt, d.mentionsPerson ? "names the subject" : "name not confirmed in body"].filter(Boolean).join(" · ")}
                            score={d.confidence}
                          >
                            <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                              {d.emails.length > 0 && <p>Emails: {d.emails.join(", ")}</p>}
                              {d.phones.length > 0 && <p>Phones: {d.phones.join(", ")}</p>}
                              {d.organizations.length > 0 && <p>Organisations: {d.organizations.join(", ")}</p>}
                              {d.otherPeople.length > 0 && <p>Other people named: {d.otherPeople.join(", ")}</p>}
                            </div>
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No public document referenced this name.</Empty>
                    )}
                  </Panel>
                </TabsContent>

                <TabsContent value="news" className="mt-3">
                  <Panel title="News mentions" subtitle="Each article states whether it is attributed to the subject or only a name match.">
                    {report.news.length ? (
                      <div className="space-y-2">
                        {report.news.map((n) => (
                          <Row
                            key={n.id}
                            title={
                              <a href={n.url} target="_blank" rel="noreferrer noopener" className="break-words text-cyan hover:underline">
                                {n.title}
                              </a>
                            }
                            badge={<Chip className={n.attribution === "attributed" ? BAND_STYLE.high! : n.attribution === "possible" ? BAND_STYLE.low! : ""}>{n.attribution}</Chip>}
                            meta={[n.publisher, n.publishedAt].filter(Boolean).join(" · ")}
                            score={n.confidence}
                          >
                            <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[11px] text-muted-foreground">
                              {n.attributionReasons.map((r) => (
                                <li key={r}>{r}</li>
                              ))}
                            </ul>
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <Empty>No news article mentioned this name.</Empty>
                    )}
                  </Panel>
                </TabsContent>

                <TabsContent value="timeline" className="mt-3">
                  <Panel title="Timeline" subtitle="Only dated events. CARIO never invents a missing date.">
                    {report.timeline.length ? (
                      <ol className="space-y-1.5">
                        {report.timeline.map((t) => (
                          <li key={t.id} className="flex flex-wrap items-baseline gap-2 rounded-md border border-panel-border bg-background/40 p-2 text-xs">
                            <span className="font-mono text-[11px] text-cyan">{t.date}</span>
                            <Chip>{t.category}</Chip>
                            <span className="min-w-0 flex-1 break-words">{t.label}</span>
                            {t.sourceUrl ? (
                              <a href={t.sourceUrl} target="_blank" rel="noreferrer noopener" className="text-[10px] text-cyan hover:underline">
                                {t.source}
                              </a>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">{t.source}</span>
                            )}
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <Empty>No dated event was published for this person.</Empty>
                    )}
                  </Panel>
                </TabsContent>

                <TabsContent value="evidence" className="mt-3">
                  <Panel title="Evidence ledger" subtitle={`${report.evidence.length} records, each with source, URL and retrieval time.`}>
                    <EvidenceList items={report.evidence} />
                    {report.evidence.length > 8 && (
                      <ul className="mt-2 space-y-1">
                        {report.evidence.slice(8).map((e) => (
                          <li key={e.id} className="flex flex-wrap items-center gap-2 rounded-md border border-panel-border bg-background/40 p-2 text-[11px]">
                            <Chip className="border-cyan/40 text-cyan">{e.kind.replace(/_/g, " ")}</Chip>
                            <span>{e.source}</span>
                            {e.sourceUrl && (
                              <a href={e.sourceUrl} target="_blank" rel="noreferrer noopener" className="ml-auto break-all text-cyan hover:underline">
                                {e.sourceUrl.slice(0, 70)}
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Panel>
                </TabsContent>

                <TabsContent value="graph" className="mt-3">
                  <Panel title="Knowledge graph" subtitle="Every edge carries the evidence record that justifies it.">
                    <ul className="space-y-1.5">
                      {report.graph.edges.map((e, i) => {
                        const node = report.graph.nodes.find((n) => n.id === e.to);
                        return (
                          <li key={`${e.from}-${e.to}-${i}`} className="flex flex-wrap items-center gap-2 rounded-md border border-panel-border bg-background/40 p-2 text-xs">
                            <Chip className="border-cyan/40 text-cyan">{e.kind.replace(/_/g, " ")}</Chip>
                            <span className="min-w-0 break-words">{node?.label ?? e.to}</span>
                            <span className="ml-auto font-mono text-[10px] text-muted-foreground">{e.confidence}%</span>
                            {e.sourceUrl && (
                              <a href={e.sourceUrl} target="_blank" rel="noreferrer noopener" className="basis-full break-all text-[10px] text-cyan hover:underline">
                                {e.source} — {e.sourceUrl}
                              </a>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    {!report.graph.edges.length && <Empty>Nothing could be linked to the subject yet.</Empty>}
                  </Panel>
                </TabsContent>

                <TabsContent value="ai" className="mt-3">
                  <Panel
                    title="AI analyst"
                    subtitle="Live web and X search on top of collected evidence. Statements without a cited link are shown as leads."
                  >
                    <p className="text-sm leading-relaxed">{report.analyst.summary}</p>
                    {report.analyst.error && <p className="mt-2 text-[11px] text-amber">{report.analyst.error}</p>}
                    <ul className="mt-3 space-y-2">
                      {report.analyst.statements.map((s, i) => (
                        <li key={`${s.section}-${i}`} className="rounded-md border border-panel-border bg-background/40 p-2.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <Chip className={CLASS_STYLE[s.classification] ?? ""}>{s.classification.replace(/_/g, " ")}</Chip>
                            <Chip>{s.section}</Chip>
                          </div>
                          <p className="mt-1 text-xs">{s.text}</p>
                          {(s.sources ?? []).length > 0 && (
                            <ul className="mt-1.5 space-y-0.5">
                              {(s.sources ?? []).map((u) => (
                                <li key={u}>
                                  <a
                                    href={u}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="break-all font-mono text-[10px] text-primary underline-offset-2 hover:underline"
                                  >
                                    {u}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                          {s.evidenceIds.length > 0 && (
                            <p className="mt-1 font-mono text-[10px] text-muted-foreground">refs: {s.evidenceIds.join(", ")}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                    {(report.analyst.ruledOut ?? []).length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-medium">Ruled out as a different subject</p>
                        <ul className="mt-1.5 space-y-1">
                          {(report.analyst.ruledOut ?? []).map((r) => (
                            <li key={r.label} className="rounded-md border border-panel-border bg-background/40 p-2 text-[11px]">
                              <span className="font-medium">{r.label}</span> — {r.reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {!report.analyst.statements.length && <Empty>The analyst produced no grounded statements for this subject.</Empty>}
                  </Panel>
                </TabsContent>


                <TabsContent value="sources" className="mt-3 space-y-3">
                  {report.funnel && (
                    <Panel title="Extraction funnel" subtitle="How many search results survived each stage of parsing.">
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {[
                          ["Search results", report.funnel.searchResults],
                          ["Pages fetched", report.funnel.pagesFetched],
                          ["Pages naming subject", report.funnel.pagesWithSubject],
                          ["Structured records", report.funnel.structuredObjects],
                          ["Documents read", report.funnel.documentsFetched],
                          ["Handles accepted", report.funnel.usernamesAccepted],
                          ["Handles rejected", report.funnel.usernamesRejected],
                        ].map(([label, value]) => (
                          <div key={String(label)} className="rounded-md border border-panel-border bg-background/40 p-2">
                            <div className="font-mono text-lg text-foreground">{String(value)}</div>
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{String(label)}</div>
                          </div>
                        ))}
                      </div>
                    </Panel>
                  )}
                  {!!report.rejected?.length && (
                    <Panel title="Rejected candidates" subtitle="Strings the parsers refused to promote into entities, and why.">
                      <ul className="space-y-1">
                        {report.rejected.slice(0, 25).map((r, i) => (
                          <li key={`${r.value}-${i}`} className="flex flex-wrap items-center gap-2 rounded-md border border-panel-border bg-background/40 p-2 text-xs">
                            <span className="font-mono">{r.value}</span>
                            <Chip>{r.reason.replace(/_/g, " ").toLowerCase()}</Chip>
                            <span className="ml-auto truncate font-mono text-[10px] text-muted-foreground">{r.source}</span>
                          </li>
                        ))}
                      </ul>
                    </Panel>
                  )}
                  <Panel title="This run" subtitle="Which sources answered, how long they took and what they returned.">

                    <ul className="space-y-1.5">
                      {report.sources.map((s) => (
                        <li key={s.id} className="flex flex-wrap items-center gap-2 rounded-md border border-panel-border bg-background/40 p-2 text-xs">
                          <Chip
                            className={
                              s.status === "ok"
                                ? BAND_STYLE.high!
                                : s.status === "error"
                                  ? "border-destructive/40 text-destructive"
                                  : ""
                            }
                          >
                            {s.status}
                          </Chip>
                          <span>{s.label}</span>
                          <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                            stage {s.stage} · {s.requests} req · {s.latencyMs}ms · {s.findings} hits
                          </span>
                          {s.error && <span className="basis-full text-[10px] text-destructive">{s.error}</span>}
                        </li>
                      ))}
                    </ul>
                  </Panel>
                  <Panel title="Source availability" subtitle="Live liveness probe of every person adapter. No investigated name is sent.">
                    {sourceHealth.isPending ? (
                      <Empty>Checking sources…</Empty>
                    ) : (
                      <ul className="grid gap-1.5 sm:grid-cols-2">
                        {(sourceHealth.data ?? []).map((h) => (
                          <li key={h.id} className="flex items-center gap-2 rounded-md border border-panel-border bg-background/40 p-2 text-xs">
                            <span className={`h-2 w-2 shrink-0 rounded-full ${h.ok ? "bg-success" : "bg-destructive"}`} />
                            <span className="min-w-0 truncate">{h.label}</span>
                            <span className="ml-auto font-mono text-[10px] text-muted-foreground">{h.latencyMs}ms</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Panel>
                </TabsContent>
              </Tabs>
            </>
          )}

          <Panel title="Saved person reports" subtitle="Every run is stored in your vault and can be reopened any time.">
            {history.isPending ? (
              <Empty>Loading…</Empty>
            ) : (history.data ?? []).length ? (
              <ul className="space-y-1.5">
                {(history.data ?? []).map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => openReport.mutate(r.id)}
                      className="flex w-full flex-wrap items-center gap-2 rounded-md border border-panel-border bg-background/40 p-2 text-left text-xs hover:border-cyan/40"
                    >
                      <span className="font-medium">{r.name}</span>
                      <Chip>{r.clusterCount} identit{r.clusterCount === 1 ? "y" : "ies"}</Chip>
                      <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                        {r.identityConfidence}% · {new Date(r.createdAt).toLocaleString()}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>No person report yet. Run one above.</Empty>
            )}
          </Panel>
        </div>
      </ModuleShell>
    </AppLayout>
  );
}
